import React, { useState, useMemo, useCallback } from 'react'
import { isEqual } from 'lodash'
import { useSelector, useDispatch } from 'react-redux'
import { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'
import { CommentState, replyComment, updateComment, deleteComment, resolveThread } from 'state/comments'
import { selectProfile, selectRequestsAreLoading } from 'state'
import { selectEditingCampaign } from 'state/campaigns'
import { parseBackendDate } from 'services/dates'
import { useGetCommentCardTitle } from 'hooks'
import CommentCard, { CommentProps } from 'particles/Comment/CommentCard'
import { useTheme } from 'particles/themes/styled'
import Popper from 'particles/Popper'
import MenuItem from 'particles/MenuItem'
import Modal from 'particles/Modal'
import ReactDOM from 'react-dom'
import { STATE_KEYS } from 'state/requests'
import { CampaignStatusControl } from 'components/Shared/Permission/enums'
import { usePermission } from 'hooks/permissions'

const replyCopies = (t: TFunction, isResolved?: boolean) => ({
    primaryButtonLabel: t('button.reply'),
    secondaryButtonLabel: t('button.cancel'),
    placeholder: isResolved ? t('campaignEditor.comment.reOpenPlaceholder') : t('placeholderText.reply'),
})

const commentItemCopies = (t: TFunction) => {
    return {
        primaryButtonLabel: t('button.save'),
        secondaryButtonLabel: t('button.cancel'),
    }
}

interface MenuItems {
    title: string
    onClick?: (e: React.MouseEvent<HTMLElement>) => void
    show: boolean
}

interface MoreActionMenu {
    anchorEl: HTMLElement | null
    menuItems: MenuItems[]
}

interface FieldCommentsCardProps {
    fieldComments: CommentState[]
    cardClassName?: string
    identifier?: string
    cardWidth: number | string
    cardMaxHeight?: number
    isFieldDeleted?: boolean
    onDeleteCommentThread?: () => void
    isResolved: boolean
    threadId: string
    onResolveThread?: () => void
    labelPrefix?: string
}

const FieldCommentsCard: React.FC<FieldCommentsCardProps> = ({
    fieldComments,
    cardClassName,
    identifier,
    cardWidth,
    cardMaxHeight,
    isFieldDeleted,
    onDeleteCommentThread,
    isResolved,
    threadId,
    onResolveThread,
    labelPrefix,
}) => {
    const [t] = useTranslation()
    const [newComment, setNewComment] = useState<string>('')
    const [moreActionPopper, setMoreActionPopper] = useState<MoreActionMenu>({ anchorEl: null, menuItems: [] })
    const [isLoading, setIsLoading] = useState(false)
    const [fieldUpdateLoading, setFieldUpdateLoading] = useState<Record<string, boolean>>({})
    const [commentToDelete, setCommentToDelete] = useState<{
        uuid: string
        isThread?: boolean
    } | null>(null)
    const campaign = useSelector(selectEditingCampaign)
    const profileDetail = useSelector(selectProfile)
    const theme = useTheme()
    const dispatch = useDispatch()
    const field: string[] | undefined = identifier?.split('_')
    const fieldIdentifier = field && field[field?.length - 1]
    const typeIdentifier = field && field[field?.length - 2]
    const cardTitle = useGetCommentCardTitle(fieldIdentifier, typeIdentifier)
    const isDeleteCommentLoading = useSelector(selectRequestsAreLoading([STATE_KEYS.DeleteCampaignComment]))
    const isResolvable = usePermission(CampaignStatusControl.ReviewCampaign)

    const onCommentChange = (comment?: string) => {
        setNewComment(comment || '')
    }

    const onPrimary = useCallback(() => {
        if (campaign && campaign.uuid && !isLoading && identifier) {
            const firstCommentUuid = fieldComments[0]?.uuid
            if (Boolean(newComment)) {
                setIsLoading(true)
                dispatch(
                    replyComment({
                        commentUuid: firstCommentUuid,
                        uuid: campaign.uuid,
                        field_identifier: identifier,
                        comment: newComment,
                        onSuccessCallback: () => {
                            setNewComment('')
                            setIsLoading(false)
                        },
                        onErrorCallback: () => {
                            setIsLoading(false)
                        },
                    }),
                )
            }
        }
    }, [identifier, campaign, isLoading, newComment, fieldComments])

    const handleResolveThread = useCallback(() => {
        if (fieldComments[0]?.uuid && campaign?.uuid && identifier) {
            dispatch(
                resolveThread({
                    uuid: campaign.uuid,
                    commentUuid: fieldComments[0].uuid,
                    resolved: !isResolved,
                    identifier: identifier,
                    threadId: threadId,
                }),
            )
            if (onResolveThread) onResolveThread()
        }
    }, [fieldComments, campaign?.uuid, isResolved, identifier, threadId])

    const handleDeleteComment = useCallback(() => {
        if (identifier && campaign?.uuid && commentToDelete) {
            dispatch(
                deleteComment({
                    uuid: campaign?.uuid,
                    commentUuid: commentToDelete.uuid,
                    isThread: commentToDelete.isThread,
                    onSuccessCallback: () => {
                        setCommentToDelete(null)
                        if (commentToDelete.isThread && onDeleteCommentThread) {
                            onDeleteCommentThread()
                        }
                    },
                    onErrorCallback: () => {
                        setCommentToDelete(null)
                    },
                }),
            )
        }
    }, [identifier, campaign, commentToDelete, isDeleteCommentLoading])

    const createMenuItem = (title: string, callback: () => void, show: boolean) => ({
        title,
        onClick: (e: React.MouseEvent<HTMLElement>) => {
            e.preventDefault()
            e.stopPropagation()
            callback()
            setMoreActionPopper({ ...moreActionPopper, anchorEl: null })
        },
        show,
    })

    const toggleMoreActionPopper = (e: React.MouseEvent<HTMLElement>, menuItems: MenuItems[]) => {
        e.preventDefault()
        e.stopPropagation()
        setMoreActionPopper({
            ...moreActionPopper,
            anchorEl: e.currentTarget,
            menuItems: menuItems,
        })
    }

    const handleCardMoreActionCallback = useCallback(e => {
        const menuItems = [
            createMenuItem(
                t('button.deleteThread'),
                () => setCommentToDelete({ uuid: fieldComments[0].uuid, isThread: true }),
                true,
            ),
        ]
        toggleMoreActionPopper(e, menuItems)
    }, [])

    const comments: CommentProps[] | null = useMemo(() => {
        if (fieldComments) {
            return fieldComments.map((comment: CommentState, index) => {
                const handleMoreAction = (e: React.MouseEvent<HTMLElement>, onEditCallBack: () => void) => {
                    const menuItems =
                        profileDetail?.uuid === comment.cprofile.uuid
                            ? [
                                  createMenuItem(t('button.editComment'), onEditCallBack, true),
                                  createMenuItem(
                                      t('button.deleteComment'),
                                      () => setCommentToDelete({ uuid: comment.uuid, isThread: false }),
                                      index > 0,
                                  ),
                              ]
                            : []
                    toggleMoreActionPopper(e, menuItems)
                }
                return {
                    profile: {
                        uuid: comment.cprofile.uuid,
                        name: comment.cprofile.name,
                    },
                    moreActionCallback:
                        profileDetail?.uuid === comment.cprofile.uuid && !isResolved ? handleMoreAction : undefined,
                    onEditClick: (updatedComment?: string) => {
                        if (
                            updatedComment &&
                            !isEqual(updatedComment, comment.comment) &&
                            identifier &&
                            campaign?.uuid
                        ) {
                            setFieldUpdateLoading({ ...fieldUpdateLoading, [comment.uuid]: true })
                            dispatch(
                                updateComment({
                                    uuid: campaign?.uuid,
                                    field_identifier: identifier,
                                    commentUuid: comment.uuid,
                                    comment: updatedComment,
                                    onSuccessCallback: () => {
                                        setNewComment('')
                                        setFieldUpdateLoading({ ...fieldUpdateLoading, [comment.uuid]: false })
                                    },
                                    onErrorCallback: () => {
                                        setFieldUpdateLoading({ ...fieldUpdateLoading, [comment.uuid]: false })
                                    },
                                }),
                            )
                        }
                    },
                    timeSlots: `${parseBackendDate(comment?.created_at, undefined, {}, 'h:mm a , d MMM')} ${
                        Boolean(comment?.edited_at) ? t('statusLabel.campaignEditor.edited') : ''
                    }`,
                    value: comment.comment,
                    uuid: comment.uuid,
                    isLoading: fieldUpdateLoading[comment.uuid],
                }
            })
        } else {
            return null
        }
    }, [fieldComments, identifier, campaign?.uuid, fieldUpdateLoading, isResolved])

    const isThreadOwner = Boolean(comments && comments[0]?.profile.uuid === profileDetail?.uuid)

    const DeleteCommentModal = () => {
        // MUI popper prevents the modal from overlaying the screen.
        // This will Portal the modal to the root element of the page
        const portalRoot = document.getElementById('root')
        return ReactDOM.createPortal(
            <Modal
                title={t(`modalWindow.${[commentToDelete?.isThread ? 'deleteThreadTitle' : 'deleteCommentTitle']}`)}
                subtext={t(
                    `modalWindow.${[commentToDelete?.isThread ? 'deleteThreadSubtitle' : 'deleteCommentSubtitle']}`,
                )}
                size="m"
                hasIcon={false}
                primaryButtonProps={{
                    label: t('button.yesDelete'),
                    isDisabled: isDeleteCommentLoading,
                    isLoading: isDeleteCommentLoading,
                }}
                secondaryButtonProps={{
                    label: t('button.noCancel'),
                    isDisabled: isDeleteCommentLoading,
                }}
                onConfirm={e => {
                    e.preventDefault()
                    if (!commentToDelete?.isThread) {
                        e.stopPropagation()
                    }
                    handleDeleteComment()
                }}
                onClose={() => {
                    setCommentToDelete(null)
                }}
                isClosable={false}
            />,
            portalRoot as Element,
        )
    }

    return (
        <>
            {identifier && comments ? (
                <CommentCard
                    replyUser={{ uuid: profileDetail?.uuid, name: profileDetail?.name }}
                    comments={comments}
                    replyCommentCopies={replyCopies(t, isResolved)}
                    commentItemCopies={commentItemCopies(t)}
                    cardTitle={`${isFieldDeleted ? t('statusLabel.campaignEditor.deleted') : ''}${labelPrefix ? labelPrefix : ''} ${cardTitle}`}
                    className={cardClassName}
                    cardWidth={typeof cardWidth === 'string' ? cardWidth : `${cardWidth}px`}
                    maxHeight={cardMaxHeight}
                    onReplyChange={onCommentChange}
                    onReplyCancelCallback={() => {
                        setNewComment('')
                    }}
                    onReplyCallback={onPrimary}
                    isReplyLoading={isLoading}
                    replyValue={newComment}
                    cardMoreActionCallback={isThreadOwner && !isResolved ? handleCardMoreActionCallback : undefined}
                    resolveConfig={{
                        onResolve: handleResolveThread,
                        isResolved: isResolved,
                        isResolvable: profileDetail?.is_internal_staff ? isResolvable : isThreadOwner,
                        tooltip: {
                            label: isResolved ? t('button.reOpen') : t('button.resolve'),
                        },
                    }}
                />
            ) : null}
            <Popper
                anchorEl={moreActionPopper.anchorEl}
                onClose={() => setMoreActionPopper({ ...moreActionPopper, anchorEl: null })}
                style={{
                    padding: theme.spacing[200],
                    minHeight: 'fit-content',
                }}
            >
                {moreActionPopper.menuItems.map(
                    (
                        item: {
                            title: string
                            onClick?: (e: React.MouseEvent<HTMLElement>) => void
                            show: boolean
                        },
                        index: number,
                    ) =>
                        item.show && (
                            <MenuItem.Text key={`menu-item-${index}`} onClick={item.onClick} text={item.title} />
                        ),
                )}
            </Popper>
            {commentToDelete ? <DeleteCommentModal /> : null}
        </>
    )
}

export default FieldCommentsCard
